import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_MS = 2500;
const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useBilling();
  const [status, setStatus] = useState({ payment_status: "pending", polls: 0 });
  const sessionId = params.get("session_id");
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus({ ...data, polls: n });
        if (data.payment_status === "paid") {
          await refresh();
          return;
        }
      } catch (e) { console.error("Payment status poll failed", e); }
      if (n < MAX_POLLS) setTimeout(() => tick(n + 1), POLL_MS);
      if (n >= MAX_POLLS && !cancelled) {
        setStatus(prev => ({ ...prev, polls: n, payment_status: prev.payment_status === "paid" ? "paid" : "timeout" }));
      }
    };
    tick(1);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const isPaid = status.payment_status === "paid";
  const isFailed = ["failed", "expired", "timeout"].includes(status.payment_status);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-surface border border-surface-3 p-10 text-center">
        {isPaid ? (
          <>
            <div className="inline-flex w-14 h-14 items-center justify-center border border-emerald-500/40 bg-emerald-500/10 mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <div className="font-display text-4xl uppercase tracking-wide text-white mt-4">You're In</div>
            <p className="text-slate-300 mt-2 text-sm">
              Payment confirmed. Your program is now on the <span className="text-amber font-semibold">
              {status.target_plan === "school" ? "School Plan" : "Pro Coach"}</span>.
            </p>
            <Button onClick={() => nav("/")} data-testid="success-go-dashboard"
              className="w-full h-11 mt-6 rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-xs">
              Go To Dashboard <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </>
        ) : isFailed ? (
          <>
            <div className="inline-flex w-14 h-14 items-center justify-center border border-red-500/40 bg-red-500/10 mx-auto">
              <XCircle className="w-7 h-7 text-red-400" />
            </div>
            <div className="font-display text-4xl uppercase tracking-wide text-white mt-4">Payment {status.payment_status}</div>
            <p className="text-slate-300 mt-2 text-sm">Please try again or contact your admin.</p>
            <Link to="/pricing" data-testid="success-back-pricing"
              className="inline-block w-full h-11 mt-6 leading-[44px] text-center bg-surface-2 border border-surface-3 hover:border-amber font-condensed uppercase tracking-[0.25em] text-xs">
              Back To Pricing
            </Link>
          </>
        ) : (
          <>
            <div className="inline-flex w-14 h-14 items-center justify-center border border-amber/40 bg-amber/10 mx-auto">
              <Loader2 className="w-7 h-7 text-amber animate-spin" />
            </div>
            <div className="font-display text-3xl uppercase tracking-wide text-white mt-4">Finalizing…</div>
            <p className="text-slate-400 mt-2 text-sm">
              Confirming your payment with Stripe. This usually takes a few seconds.
            </p>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-4">
              Check {status.polls}/{MAX_POLLS}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8" data-testid="payment-cancel-page">
      <div className="max-w-md w-full bg-surface border border-surface-3 p-10 text-center">
        <div className="inline-flex w-14 h-14 items-center justify-center border border-slate-500/40 bg-slate-500/10 mx-auto">
          <XCircle className="w-7 h-7 text-slate-300" />
        </div>
        <div className="font-display text-4xl uppercase tracking-wide text-white mt-4">Checkout Canceled</div>
        <p className="text-slate-300 mt-2 text-sm">No charge was made. Your plan is unchanged.</p>
        <Link to="/pricing" data-testid="cancel-back-pricing"
          className="inline-block w-full h-11 mt-6 leading-[44px] text-center bg-amber hover:bg-amber-600 text-[#0B0D12] font-condensed uppercase tracking-[0.25em] text-xs">
          Back To Pricing
        </Link>
      </div>
    </div>
  );
}
